import React, { useState, useRef, useEffect } from 'react';
import { FaBell, FaBellSlash } from 'react-icons/fa';
import { useNotification } from '../../context/NotificationContext';
import NotificationDropdown from './NotificationDropdown';
import styles from './NotificationBell.module.css';

/**
 * NotificationBell Component
 * 
 * Displays a bell icon with unread count badge and handles dropdown visibility.
 * Features:
 * - Real-time unread count updates
 * - Visual pulse animation for new notifications
 * - Click outside to close dropdown
 * - Keyboard accessibility
 * - Responsive design
 */
const NotificationBell = ({ className = '' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hasNewNotifications, setHasNewNotifications] = useState(false);
  const [previousCount, setPreviousCount] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const dropdownRef = useRef(null);
  const bellButtonRef = useRef(null);
  
  const { 
    unreadCount, 
    fetchUnreadCount, 
    preferences,
    error 
  } = useNotification();

  // Check if mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    const handleEscapeKey = (event) => {
      if (event.key === 'Escape' && isOpen) {
        setIsOpen(false);
        bellButtonRef.current?.focus();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscapeKey);
      
      // Prevent body scroll on mobile when dropdown is open
      if (isMobile) {
        document.body.style.overflow = 'hidden';
      }
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
      
      // Restore body scroll
      if (isMobile) {
        document.body.style.overflow = '';
      }
    };
  }, [isOpen, isMobile]);

  // Detect new notifications and trigger pulse animation
  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      // Skip animation on initial mount, just record the count
      isInitialMount.current = false;
      setPreviousCount(unreadCount);
      return;
    }
    
    if (unreadCount > previousCount) {
      setHasNewNotifications(true);
      
      const timer = setTimeout(() => {
        setHasNewNotifications(false);
      }, 3000);
      
      setPreviousCount(unreadCount);
      return () => clearTimeout(timer);
    }
    
    setPreviousCount(unreadCount);
  }, [unreadCount, previousCount]);

  // Handle bell click
  const handleBellClick = () => {
    setIsOpen(!isOpen);
    setHasNewNotifications(false);
    
    // Refresh unread count when opening
    if (!isOpen) {
      fetchUnreadCount();
    }
  };

  // Handle keyboard navigation
  const handleKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleBellClick();
    }
  };

  // Don't render if user has globally opted out
  if (preferences?.globalOptOut) {
    return null;
  }

  // Show normal bell even if there's an error (backend not ready)
  // We'll just disable the dropdown functionality but keep the bell visible
  const isDisabled = false; // Always show the bell icon
  const BellIcon = FaBell;

  return (
    <div className={`${styles.notificationBell} ${className}`} ref={dropdownRef}>
      <button
        ref={bellButtonRef}
        className={`${styles.bellButton} ${hasNewNotifications ? styles.pulse : ''} ${isOpen ? styles.active : ''} ${isDisabled ? styles.disabled : ''}`}
        onClick={handleBellClick}
        onKeyDown={handleKeyDown}
        disabled={isDisabled}
        aria-label={
          isDisabled 
            ? 'Notifications unavailable'
            : `Notifications ${unreadCount > 0 ? `(${unreadCount} unread)` : ''}`
        }
        aria-expanded={isOpen}
        aria-haspopup="true"
        title={
          isDisabled 
            ? 'Notifications unavailable' 
            : unreadCount > 0 
              ? `${unreadCount} unread notification${unreadCount === 1 ? '' : 's'}`
              : 'No unread notifications'
        }
      >
        <BellIcon className={styles.bellIcon} />
        
        {/* Unread count badge */}
        {unreadCount > 0 && !isDisabled && (
          <span 
            className={styles.badge}
            aria-label={`${unreadCount} unread notifications`}
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
        
        {/* New notification indicator dot */}
        {hasNewNotifications && !isDisabled && (
          <span className={styles.newIndicator} aria-hidden="true" />
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <>
          {/* Mobile backdrop */}
          {isMobile && (
            <div 
              className={styles.mobileBackdrop}
              onClick={() => setIsOpen(false)}
              aria-hidden="true"
            />
          )}
          <NotificationDropdown 
            onClose={() => setIsOpen(false)}
            onNotificationClick={() => setIsOpen(false)}
          />
        </>
      )}
    </div>
  );
};

export default NotificationBell;
